import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
  Box,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../../hooks/useAuth';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export const SessionTimeoutDialog = () => {
  const { isAuthenticated, expirationTime, extendSession, clearToken } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const alertTimerRef = useRef(null);
  const tokenTimerRef = useRef(null);
  const intervalRef = useRef(null);

  const handleLogout = useCallback(() => {
    clearToken();
    navigate('/');
  }, [clearToken, navigate]);

  const handleExtend = async () => {
    try {
      await extendSession();
      setOpen(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (e) {
      console.error('Extend session failed', e);
      setOpen(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    if (tokenTimerRef.current) {
      clearTimeout(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }

    if (!isAuthenticated || !expirationTime) {
      setOpen(false);
      return;
    }

    const now = Date.now();
    const timeToExpiration = expirationTime - now;

    if (timeToExpiration <= 0) {
      handleLogout();
      return;
    }

    const showAlertDelay = Math.max(0, timeToExpiration - 60000);
    alertTimerRef.current = setTimeout(() => {
      setOpen(true);
      const updateRemaining = () => {
        const sec = Math.max(0, Math.ceil((expirationTime - Date.now()) / 1000));
        setRemainingSeconds(sec);
        if (sec <= 0 && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
      updateRemaining();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      intervalRef.current = setInterval(updateRemaining, 1000);
      alertTimerRef.current = null;
    }, showAlertDelay);

    tokenTimerRef.current = setTimeout(() => {
      handleLogout();
      tokenTimerRef.current = null;
    }, timeToExpiration);

    return () => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
      if (tokenTimerRef.current) clearTimeout(tokenTimerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      alertTimerRef.current = null;
      tokenTimerRef.current = null;
      intervalRef.current = null;
    };
  }, [isAuthenticated, expirationTime, handleLogout]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
    >
      <DialogContent>
        <DialogContentText id="session-timeout-description">
          Tu sesión está a punto de expirar en un minuto.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
          <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" color="textSecondary">
            {formatTime(remainingSeconds)}
          </Typography>
        </Box>
        <Button onClick={handleExtend} color="primary" variant="contained">
          Extender Sesión
        </Button>
        <Button
          onClick={() => {
            setOpen(false);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }}
          color="secondary"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
